import type { CSSProperties, RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

const PORTAL_OFFSET_FALLBACK = 8;
const MENU_MIN_WIDTH = 250;
const MENU_MAX_HEIGHT = 365;
const VIEWPORT_MARGIN = 16;

const resolvePortalOffset = () => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--scale-2')
    .trim();

  const parsed = Number.parseFloat(raw);

  return Number.isFinite(parsed) ? parsed : PORTAL_OFFSET_FALLBACK;
};

const LANDMARK_SELECTOR = [
  'main',
  '[role="main"]',
  'header',
  '[role="banner"]',
  'footer',
  '[role="contentinfo"]',
  'nav',
  '[role="navigation"]',
  'aside',
  '[role="complementary"]',
  '[role="search"]',
  '[role="region"]',
  'dialog',
  '[role="dialog"]',
  '[role="alertdialog"]',
].join(', ');

const fixedContainingBlockProperties = new Set([
  'backdrop-filter',
  'contain',
  'filter',
  'perspective',
  'rotate',
  'scale',
  'transform',
  'translate',
]);

const hasNonDefaultValue = (value: string | undefined) =>
  !!value && value !== 'none';

const establishesFixedContainingBlock = (element: HTMLElement) => {
  const style = getComputedStyle(element);
  const containment = style.contain?.split(/\s+/) ?? [];
  const willChange = style.willChange?.split(/,\s*/) ?? [];

  return (
    [
      style.backdropFilter,
      style.filter,
      style.perspective,
      style.rotate,
      style.scale,
      style.transform,
      style.translate,
    ].some(hasNonDefaultValue) ||
    containment.some((value) =>
      ['content', 'layout', 'paint', 'strict'].includes(value)
    ) ||
    style.contentVisibility === 'auto' ||
    willChange.some((value) => fixedContainingBlockProperties.has(value))
  );
};

const hasNonViewportFixedContainingBlock = (
  element: HTMLElement | null
): boolean => {
  if (!element || element === document.body) {
    return false;
  }

  return (
    establishesFixedContainingBlock(element) ||
    hasNonViewportFixedContainingBlock(element.parentElement)
  );
};

const resolvePortalContainer = (trigger: HTMLElement | null) => {
  const landmark = trigger?.closest<HTMLElement>(LANDMARK_SELECTOR);

  return landmark && !hasNonViewportFixedContainingBlock(landmark)
    ? landmark
    : document.body;
};

export const useMenuPortalPosition = (
  isOpen: boolean,
  triggerRef: RefObject<HTMLElement>,
  menuRef: RefObject<HTMLElement>,
  isFullWidth = false,
  shouldMatchTriggerWidth = false
) => {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );
  const [style, setStyle] = useState<CSSProperties | null>(null);

  useEffect(() => {
    setPortalContainer(resolvePortalContainer(triggerRef.current));
  }, [triggerRef]);

  const update = useCallback(() => {
    if (!isOpen || !triggerRef.current) {
      setStyle(null);
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const offset = resolvePortalOffset();
    const viewportWidth =
      typeof window === 'undefined' || !window.innerWidth
        ? MENU_MIN_WIDTH
        : window.innerWidth;
    const viewportHeight =
      typeof window === 'undefined' || !window.innerHeight
        ? MENU_MAX_HEIGHT + VIEWPORT_MARGIN * 2
        : window.innerHeight;

    // Measure the rendered menu so it only shifts as far as it needs to in order
    // to stay on screen. This keeps the menu anchored to its trigger instead of
    // being pushed away by an assumed minimum width.
    const menuWidth =
      menuRef.current?.getBoundingClientRect().width || MENU_MIN_WIDTH;

    const maxLeft = Math.max(
      VIEWPORT_MARGIN,
      viewportWidth - menuWidth - VIEWPORT_MARGIN
    );
    const left = Math.min(Math.max(rect.left, VIEWPORT_MARGIN), maxLeft);

    const maxMenuWidth = viewportWidth - left - VIEWPORT_MARGIN;
    const menuHeight =
      menuRef.current?.scrollHeight ||
      menuRef.current?.getBoundingClientRect().height ||
      MENU_MAX_HEIGHT;
    const offsetBelow = isFullWidth ? 0 : offset;
    const availableBelow = Math.max(
      0,
      viewportHeight - rect.bottom - offsetBelow - VIEWPORT_MARGIN
    );
    const availableAbove = Math.max(0, rect.top - offset - VIEWPORT_MARGIN);
    const shouldOpenAbove =
      availableBelow < Math.min(menuHeight, MENU_MAX_HEIGHT) &&
      availableAbove > availableBelow;
    const availableHeight = shouldOpenAbove ? availableAbove : availableBelow;
    const renderedHeight = Math.min(
      menuHeight,
      MENU_MAX_HEIGHT,
      availableHeight
    );

    setStyle({
      left,
      top: shouldOpenAbove
        ? rect.top - offset - renderedHeight
        : rect.bottom + offsetBelow,
      ...(availableHeight < MENU_MAX_HEIGHT
        ? { maxHeight: availableHeight }
        : {}),
      ...(shouldMatchTriggerWidth
        ? { width: rect.width }
        : { maxWidth: maxMenuWidth }),
    });
  }, [isOpen, isFullWidth, shouldMatchTriggerWidth, triggerRef, menuRef]);

  useLayoutEffect(update, [update]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen, update]);

  return { portalContainer, portalStyle: style };
};
